import * as FavCache from '@finn-no/favorite-api-browser-client';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import { messages as daMessages } from '../locales/da';
import { messages as enMessages } from '../locales/en';
import { messages as fiMessages } from '../locales/fi';
import { messages as nbMessages } from '../locales/nb';
import { messages as svMessages } from '../locales/sv';
import HeartButton from './HeartButton';
import SaveAdModal from './SaveAdModal';
import { activateI18n } from './i18n';
import type { variant } from './types';

type ForwardedProps = {
    /** Used in the login flow, it's the current url by default */
    redirectUrl?: string;
};

type ApiOptions = {
    userId?: number | string;
    fetch?: (...args: any[]) => Promise<Response>;
    baseUri?: string;
    modalAttach?: HTMLElement;
    renderServerSide?: boolean;
} & ForwardedProps;

let instance: FavCache.FavoriteBrowserCache;
function getApiInstance(p: ApiOptions): FavCache.FavoriteBrowserCache {
    if (!instance) {
        instance = new FavCache.FavoriteBrowserCache(
            p.userId || '',
            undefined,
            undefined,
            1000 * 60 * 5, // 5 MIN
            p.fetch,
            p.baseUri,
        );
    }
    return instance;
}

export type SaveAdProps = {
    /* Add your own custom styles to the button element */
    buttonClass?: string;
    /** 'Ad' or 'Hotel' */
    itemType: FavCache.ItemType;
    itemId: number;
    toggled?: boolean;
    /** Show how many times the ad has been favorited. Works best with variant="button" */
    renderNumFavs?: boolean;
    /** If true it'll set focus on mobile bottom sheet, so keyboard does not open */
    isMobile: boolean;
    /** Will open the modal/bottom sheet. Make sure only a single Heart component is set to open */
    isOpen?: boolean;
    /** By default it's 'heart' */
    variant: variant;
    /** Optional locale for i18n */
    locale?: 'en' | 'nb' | 'fi' | 'da' | 'sv';
} & ForwardedProps;

export default function createComponent({
    redirectUrl: _redirectUrl,
    ...options
}: ApiOptions): React.FC<SaveAdProps> {
    const api = getApiInstance(options);

    const { userId } = options;
    const SaveAd = forwardRef((props: SaveAdProps, forwardRef) => {
        const {
            buttonClass,
            itemType,
            itemId,
            redirectUrl = _redirectUrl,
            renderNumFavs,
            variant,
            isMobile,
            locale,
        } = props;
        const [render, startRender] = useState(false);
        const [toggled, setToggled] = useState(props.toggled ?? false);
        const [isOpen, setOpen] = useState(props.isOpen ?? false);
        const [numFavs, setNumFavs] = useState(0);
        const restoreFocusRef = useRef<HTMLButtonElement | null>(null);

        activateI18n(
            {
                enMessages,
                nbMessages,
                fiMessages,
                daMessages,
                svMessages,
            },
            locale,
        );

        useEffect(() => {
            if (userId) {
                const cleanup = api.getFavoriteState(
                    itemType,
                    itemId,
                    (toggled) => setToggled(toggled),
                );

                if (cleanup) {
                    return () => cleanup();
                }
            }
        }, [itemType, itemId]);

        useEffect(() => {
            if (renderNumFavs) {
                const cleanup = api.subscribeToRecount(
                    itemType,
                    itemId,
                    setNumFavs,
                );

                if (cleanup) {
                    return () => cleanup();
                }
            }
        }, [itemType, itemId, renderNumFavs]);

        useEffect(() => {
            startRender(true);
        }, []);

        // Focus is only restored by the Modal/BottomSheet if an element already had focus before opening
        // On Safari in particular buttons won't receive focus when clicking on them.
        const restoreFocus = useCallback(() => {
            setTimeout(() => {
                if (
                    restoreFocusRef.current &&
                    document.activeElement !== restoreFocusRef.current
                ) {
                    // The scrolling behavior when calling .focus() is very unreliable and incosistent between browsers
                    // The call below will ensure a consistent and satisfactory UX by scrolling things into view ahead of calling .focus()
                    scrollIntoView(restoreFocusRef.current, {
                        block: 'center',
                        inline: 'center',
                        scrollMode: 'if-needed',
                    });
                    restoreFocusRef.current.focus();
                }
            }, 0);
        }, []);

        const sharedProps = {
            itemType,
            itemId,
            api,
            isOpen,
            setOpen,
            redirectUrl,
            restoreFocus,
            toggled,
            userId,
            isMobile,
        };

        return options.renderServerSide || render ? (
            <>
                <HeartButton
                    className={buttonClass}
                    numFavs={numFavs}
                    onClick={() => setOpen(true)}
                    ref={(element) => {
                        restoreFocusRef.current = element;
                        if (forwardRef) {
                            if (typeof forwardRef === 'function') {
                                forwardRef(element);
                            } else {
                                forwardRef.current = element;
                            }
                        }
                    }}
                    toggled={toggled}
                    variant={variant}
                />
                {typeof window !== 'undefined'
                    ? ReactDOM.createPortal(
                          <SaveAdModal {...sharedProps} />,
                          options.modalAttach || document.body,
                      )
                    : ''}
            </>
        ) : (
            <span />
        );
    });

    return SaveAd;
}
